import type { SortDescriptor } from "react-stately";

import { useCallback, useState } from "react";

type TeamLeadSectionType =
  | "awaiting-reply"
  | "in-queue"
  | "others"
  | "processing";

interface SectionState {
  currentPage: number;
  itemsPerPage: number;
  sortDescriptor: SortDescriptor;
}

const DEFAULT_ITEMS_PER_PAGE = 10;

/**
 * Default sort configurations per section based on acceptance criteria:
 * - Processing: Sort by created date (newest first)
 * - Awaiting Reply: Sort by when added to status (newest first) = updatedAt
 * - In Queue: Sort by when added to status (newest first) = updatedAt
 * - Others: Sort by when added to status (newest first) = updatedAt
 */
const SECTION_SORT_DEFAULTS: Record<
  TeamLeadSectionType,
  { column: string; direction: "ascending" | "descending" }
> = {
  "awaiting-reply": {
    column: "updatedAt",
    direction: "descending",
  },
  "in-queue": {
    column: "updatedAt",
    direction: "descending",
  },
  others: {
    column: "updatedAt",
    direction: "descending",
  },
  processing: {
    column: "createdAt",
    direction: "descending",
  },
};

export function useTeamLeadSectionStates() {
  const [sectionStates, setSectionStates] = useState<
    Record<TeamLeadSectionType, SectionState>
  >({
    "awaiting-reply": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["awaiting-reply"],
    },
    "in-queue": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["in-queue"],
    },
    others: {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS.others,
    },
    processing: {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS.processing,
    },
  });

  const createSectionHandlers = useCallback(
    (section: TeamLeadSectionType) => ({
      onItemsPerPageChange: (itemsPerPage: number) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            currentPage: 1, // Reset to first page when changing items per page
            itemsPerPage,
          },
        }));
      },
      onPageChange: (page: number) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            currentPage: page,
          },
        }));
      },
      onSortChange: (sortDescriptor: SortDescriptor) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            sortDescriptor,
          },
        }));
      },
    }),
    [],
  );

  return {
    createSectionHandlers,
    sectionStates,
  };
}
